import { memo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ContextModalProps } from "@mantine/modals";
import { z } from "zod";

import { Button, Group, Stack, TextInput } from "@mantine/core";

import { useCreateCustomViewMutation } from "modules/core/hooks/mutations/custom_views/useCreateCustomViewMutation";

import { CustomView } from "modules/core/types/entities";

type CreateCustomViewModalProps = ContextModalProps<{
  view: Omit<CustomView, "id" | "title" | "user_id">;
  onSubmit: (view: CustomView) => void;
}>;

const CreateCustomViewModal = (props: CreateCustomViewModalProps) => {
  const { id, context, innerProps } = props;

  const { mutate: createView, isLoading: isCreating } =
    useCreateCustomViewMutation();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<{ title: string }>({
    defaultValues: { title: "" },
    resolver: zodResolver(
      z.object({
        title: z.string().min(1, "Title is required")
      })
    )
  });

  const submitHandler = (data: { title: string }) => {
    createView(
      {
        view: { ...innerProps.view, title: data.title }
      },
      {
        onSuccess: newView => {
          context.closeModal(id);
          innerProps.onSubmit?.(newView);
        }
      }
    );
  };

  return (
    <form onSubmit={handleSubmit(submitHandler)}>
      <Stack>
        <TextInput
          data-autofocus
          label="View Title"
          placeholder="View title..."
          {...register("title")}
          error={errors.title?.message}
          disabled={isCreating}
        />

        <Group justify="flex-end">
          <Button
            variant="light"
            color="dark"
            size="xs"
            disabled={isCreating}
            onClick={() => {
              context.closeModal(id);
            }}
          >
            Cancel
          </Button>

          <Button color="violet" type="submit" size="xs" loading={isCreating}>
            Create
          </Button>
        </Group>
      </Stack>
    </form>
  );
};

export default memo(CreateCustomViewModal);
